import { Extension, ExtensionStyle } from "@/types/dto/pdp/pdp-itinerary";
import { ColletteChip, ColletteIconButton } from "@collette-travel/collette-common-controls";
import { Box, Stack, Typography, Modal } from "@mui/material";
import Image from "next/image";
import React from "react";
import { DirectionsRun, Close } from "@mui/icons-material";
import ActivityLevelIndicator from "@/components/common/activity-level-indicator/activity-level-indicator";
import { useTranslations } from "next-intl";
import useScrollLock from "@/hooks/scroll-lock/use-scroll-lock";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import { stripTags } from "@/utils/content-utils";

interface EnhanceYourTourModalProps {
  isOpen: boolean;
  onClose: () => void;
  extension: Extension;
  selectedExtensionStyle?: ExtensionStyle;
  type: "pre" | "post";
}

const EnhanceYourTourModal = ({
  isOpen,
  onClose,
  extension,
  selectedExtensionStyle,
  type
}: EnhanceYourTourModalProps) => {
  const t = useTranslations("PDP");
  useScrollLock(isOpen);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="view-details-modal-title"
      aria-describedby={extension.description ? "view-details-modal-description" : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
      disableScrollLock
    >
      <Stack
        sx={{
          borderRadius: "1rem",
          backgroundColor: "background.default",
          position: "relative",
          padding: { xs: "2.4rem", md: "4rem 6rem" },
          gap: "2.4rem",
          maxWidth: { xs: "90%", sm: "60rem", md: "100rem" },
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: 24
        }}
      >
        {/* Close Button */}
        <ColletteIconButton
          onClick={onClose}
          testId="view-details-modal-close-button"
          sx={{
            position: "absolute",
            top: { xs: 8, md: 24 },
            right: { xs: 8, md: 24 },
            zIndex: 1
          }}
        >
          <Close />
        </ColletteIconButton>

        {/* Main Content: Image + Details */}
        <Stack
          sx={{
            display: "flex",
            gap: "2rem",
            alignItems: "flex-start",
            flexDirection: { xs: "column", md: "row" }
          }}
        >
          {/* Left Column: Image */}
          {extension.imageUrl && (
            <Box
              sx={{
                position: "relative",
                borderRadius: "2rem",
                overflow: "hidden",
                flexShrink: 0,
                backgroundColor: "grey.200",
                width: "30rem",
                height: "20rem"
              }}
            >
              <ColletteChip
                color="info"
                sx={{
                  fontSize: "1.6rem !important",
                  position: "absolute",
                  top: 12,
                  left: 12,
                  zIndex: 5,
                  fontWeight: 500,
                  borderRadius: "1rem"
                }}
                testId="optional-extension-chip"
                label={t("Optional Extension")}
              />
              <Image
                src={extension.imageUrl}
                data-testid={`${type}-modal-image`}
                alt={extension.imageAltTag || "extension-image"}
                fill
                style={{ objectFit: "cover" }}
                unoptimized
              />
            </Box>
          )}

          {/* Right Column: Title + Icon Details + Description */}
          <Stack gap="2.4rem">
            {/* Title */}
            <Typography
              id="view-details-modal-title"
              component="h2"
              sx={{
                fontSize: "2rem",
                fontWeight: 600,
                lineHeight: 1.3,
                color: "primary.main",
                margin: 0
              }}
              data-testid={`${type}-modal-title`}
            >
              {t(type === "pre" ? "Pre-Tour Extension" : "Post-Tour Extension")}: {extension.title}
            </Typography>

            {/* Icon Detail Rows + Description */}
            <Stack gap="2.4rem">
              {/* Icon Detail Rows */}
              <Stack gap="1.2rem">
                {/* Price */}
                {extension.price && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.8rem",
                      alignItems: "center"
                    }}
                  >
                    <ConfirmationNumberOutlinedIcon />
                    <Typography
                      sx={{
                        fontSize: "1.6rem",
                        fontWeight: 500,
                        lineHeight: 1.5,
                        color: "text.primary",
                        letterSpacing: "0.4px"
                      }}
                      data-testid={`${type}-modal-price`}
                    >
                      {extension.price} {t("per person")}
                    </Typography>
                  </Box>
                )}

                {/* Activity Level */}
                {extension.activityLevelDescription && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.8rem",
                      alignItems: "center",
                      flexWrap: "wrap"
                    }}
                  >
                    <DirectionsRun sx={{ fontSize: "2.4rem", color: "text.primary" }} />
                    <Typography
                      sx={{
                        fontSize: "1.6rem",
                        fontWeight: 500,
                        lineHeight: 1.5,
                        color: "text.primary",
                        letterSpacing: "0.4px"
                      }}
                      data-testid={`${type}-modal-activity-level`}
                    >
                      {t("Activity Level")}: {extension.activityLevelDescription}
                    </Typography>
                    <ActivityLevelIndicator
                      activityLevelNumber={extension.activityLevelNumber ?? 1}
                      size="small"
                    />
                  </Box>
                )}

                {/* Duration */}
                {extension.days && extension.days.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.8rem",
                      alignItems: "center"
                    }}
                  >
                    <EventAvailableOutlinedIcon />
                    <Typography
                      sx={{
                        fontSize: "1.6rem",
                        fontWeight: 500,
                        lineHeight: 1.5,
                        color: "text.primary",
                        letterSpacing: "0.4px"
                      }}
                      data-testid={`${type}-modal-duration`}
                    >
                      {extension.days.length} {t("Nights")}
                    </Typography>
                  </Box>
                )}

                {/* Extension Style */}
                {extension.extensionStyle && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.8rem",
                      alignItems: "center"
                    }}
                  >
                    {selectedExtensionStyle?.imageUrl && selectedExtensionStyle?.imageAltTag && (
                      <Image
                        data-testid={`${type}-modal-extension-style-image`}
                        src={selectedExtensionStyle.imageUrl}
                        alt={selectedExtensionStyle.imageAltTag}
                        height={24}
                        width={24}
                      />
                    )}
                    <Typography
                      data-testid={`${type}-modal-extension-style`}
                      sx={{
                        fontSize: "1.6rem",
                        fontWeight: 500,
                        lineHeight: 1.5,
                        color: "text.primary",
                        letterSpacing: "0.4px"
                      }}
                    >
                      {t("Extension Style")}: {extension.extensionStyle}
                    </Typography>
                  </Box>
                )}
              </Stack>

              {/* Description */}
              {extension.description && (
                <Typography
                  id="view-details-modal-description"
                  sx={{
                    fontSize: "1.6rem",
                    fontWeight: 400,
                    lineHeight: 1.6,
                    color: "text.primary"
                  }}
                  data-testid={`${type}-modal-description`}
                >
                  {extension.description}
                </Typography>
              )}
              {selectedExtensionStyle && (
                <Typography
                  sx={{
                    fontSize: "1.6rem",
                    fontWeight: 400,
                    lineHeight: 1.6,
                    color: "text.primary"
                  }}
                  data-testid={`${type}-modal-extension-style-description`}
                >
                  <Typography data-testid={`${type}-modal-extension-style-name`} component="span" sx={{ fontWeight: 700 }}>
                    {selectedExtensionStyle.name}:
                  </Typography>{" "}
                  <Typography data-testid={`${type}-modal-extension-style-description-text`} component="span">
                    {stripTags(selectedExtensionStyle.description)}
                  </Typography>
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default EnhanceYourTourModal;
